/* ============================================================
   filtrous-hero-nine.js  —  homepage hero + featured rail
   Rewritten 2026-09-13 for HIG audit findings F1, F2, F7.

   The hero headline is now live text instead of a bitmap, and
   the two stacked promo cards are a scroll-snapped rail of
   tiles. The rail has no autoplay and no timers, so WCAG
   2.2.2 (Pause, Stop, Hide) does not apply. Scrolling is
   native, so it works with a swipe, a trackpad, and the
   keyboard even if this script never runs.

   Tile copy and destinations live in TILES below — that is the
   only place to edit to add, remove, or reorder a tile.
   ============================================================ */
(function () {
  "use strict";

  function esc(s) {
    return String(s == null ? "" : s)
      .replace(/&/g, "&amp;").replace(/</g, "&lt;")
      .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }

  function cfg(k) {
    return (window.FV3_CONFIG && window.FV3_CONFIG[k]) || "";
  }

  /* The illustration is a new theme asset and is not in
     FV3_CONFIG, so its URL is derived from a configured
     sibling. Adding
        heroIllustrationUrl: "{{ 'filtrous-hero-illustration.png' | asset_url }}",
     to window.FV3_CONFIG in sections/filtrous-v3-app.liquid
     would make this explicit; until then this resolves it. */
  function assetUrl(file) {
    var base = cfg("bannerAccessibleUrl") || cfg("logoUrl") || "";
    var m = base.match(/^([\s\S]*\/assets\/)/);
    return m ? m[1] + file : "";
  }

  var HEADLINE = { lead: "We make science ", em: "accessible." };
  var SUBHEAD  = "Get your lab consumables from Filtrous today.";
  var GUIDE_HREF = "/pages/filtrous-field-guide-v2";

  /* tone:   "dark" (navy ground, mint accent) or "cream".
     art:    a key in FV3_CONFIG.
     artSrc: a literal image URL, used when the image isn't in
             FV3_CONFIG. Takes precedence over `art`.
     when:   optional end-date line above the CTA.
     cta:    single CTA label; the whole tile becomes the link.
     ctas:   [{label, href, aria}] for an offer with more than
             one destination. The tile is then a plain container
             and each pill is its own link — a link cannot be
             nested inside another link, and one wrapper link
             could not describe two destinations honestly.
     Omit both art keys for a text-only tile.
     Tones alternate cream / dark / cream / dark down the list —
     keep that going if you add one. */
  var TILES = [
    {
      /* Was a full-width strip above the hero (#fi-b3g2-home),
         which collapsed at 375px and duplicated a promotion the
         rail already handles. It runs as a tile now; the strip
         is hidden in filtrous-hero-nine.css.

         The offer covers exactly two SKUs — PTF-GS-0010
         (universal fit) and PTF-LS-0010 (Rainin LTS), both
         tagged Buy3Get2 in the admin — so the copy names
         both fits instead of implying every 10 µL tip
         qualifies, and each fit gets its own link.

         The strip's old CTA pointed at /pages/buy-3-get-2,
         which 404s. /collections/all/buy3get2 returns 200 but
         renders an empty state, so these link straight to the
         two products. Build a real Buy3Get2 collection and
         this can collapse back to a single CTA. */
      tone: "cream",
      head: ["Buy 3,", "get 2 free."],
      body: "Universal fit or Rainin LTS 10 µL filter tips.",
      when: "Ends September 30",
      ctas: [
        { label: "Universal fit",
          href:  "/products/universal-filter-tips-sterile-960-unit",
          aria:  "Universal fit — shop 10 µL filter tips, buy 3 get 2 free" },
        { label: "Rainin LTS",
          href:  "/products/lts-rainin-pipette-tip-filter-racked-sterile-10ul-960-unit",
          aria:  "Rainin LTS — shop 10 µL filter tips, buy 3 get 2 free" }
      ],
      /* Single tip illustration on the cream tile's own #FFFDF7,
         so it needs no frame (2026-09-14, Adelaide). New asset,
         so the un-versioned derived URL has no stale CDN copy. */
      /* Both Buy3Get2 SKUs, cut out: PTF-GS-0010 (clear, universal)
         and PTF-LS-0010 (green, Rainin LTS), side by side. */
      artAsset: "fi-b3g2-skus-cut2.png",
      w: 331, h: 900
    },
    {
      tone: "dark",
      href: "/collections/extraction-purification-v2",
      head: ["Blue Dragon", "extraction kits"],
      body: "Everything you need in one kit.",
      cta:  "Shop the kits",
      /* Cutout art (2026-09-14, Adelaide): background removed so the
         product floats on the tile - same for the tiles below. */
      artAsset: "fi-art-bluedragon-cut3.png", w: 1000, h: 746
    },
    {
      tone: "cream",
      href: "/collections/on-sale-v2",
      head: ["Buy one,", "get one free."],
      body: "Stock up while supplies last. Limited time only.",
      cta:  "Shop the sale",
      artAsset: "fi-art-bogo-cut.png", w: 583, h: 433
    },
    {
      tone: "dark",
      href: "/pages/quality-v2",
      head: ["A COA for", "every lot."],
      body: "Certificates of analysis, traceable by lot number.",
      cta:  "Check a lot",
      artAsset: "fi-art-coa-cut2.png", w: 640, h: 868
    },
    {
      /* 2026-09-14, Adelaide: the generic industries card is out;
         the two industry pushes are Peptide testing and
         Nutraceutical (the cosmetic-nutra-food landing page). */
      tone: "cream",
      href: "/pages/peptides",
      head: ["Peptide", "testing."],
      body: "155 consumables for synthesis and QC \u2014 low-binding, HPLC-grade.",
      cta:  "Explore peptide testing",   /* was "Explore peptides" (2026-09-15, Adelaide) */
      artAsset: "fi-art-peptides-cut.png", w: 700, h: 856
    },
    {
      tone: "dark",
      href: "/pages/cosmetic-nutra-food",
      head: ["Nutraceutical", "testing."],
      body: "Residues, actives and micro \u2014 the QuEChERS-to-HPLC pipeline, stocked.",
      cta:  "Explore nutraceutical",
      artAsset: "fi-art-nutra-cut.png", w: 700, h: 700
    }
  ];

  function tileHTML(t) {
    var art   = t.artSrc || (t.artAsset ? assetUrl(t.artAsset) : "") || (t.art ? cfg(t.art) : "");
    var multi = !!(t.ctas && t.ctas.length);
    var cls   = "h9-tile h9-tile--" + (t.tone === "cream" ? "cream" : "dark") +
                (art ? "" : " h9-tile--noart") +
                (multi ? " h9-tile--multi" : "");

    var inner =
      '<h3 class="h9-th">' + esc(t.head[0]) + ' <b>' + esc(t.head[1]) + '</b></h3>' +
      '<span class="h9-trule" aria-hidden="true"></span>' +
      '<p class="h9-tb">' + esc(t.body) + '</p>' +
      (t.when ? '<p class="h9-twhen">' + esc(t.when) + '</p>' : "");

    if (multi) {
      /* Each pill carries an aria-label that begins with its own
         visible text, so the accessible name contains the label
         (WCAG 2.5.3) while still saying what the link does when
         it is read out of context in a links list. */
      inner += '<span class="h9-pills">' +
        t.ctas.map(function (c, i) {
          return '<a class="h9-pill' + (i ? " h9-pill--alt" : "") + '" href="' +
                 esc(c.href) + '"' +
                 (c.aria ? ' aria-label="' + esc(c.aria) + '"' : "") + '>' +
                 esc(c.label) + '</a>';
        }).join("") + '</span>';
    } else {
      inner += '<span class="h9-pill">' + esc(t.cta) + '</span>';
    }

    if (art) {
      inner += '<img class="h9-tart' + (t.artFramed ? " h9-tart--framed" : "") +
               '" src="' + esc(art) + '" alt=""' +
               (t.w ? ' width="' + t.w + '"' : "") +
               (t.h ? ' height="' + t.h + '"' : "") +
               ' loading="lazy" decoding="async" />';
    }

    return multi
      ? '<div class="' + cls + '">' + inner + '</div>'
      : '<a class="' + cls + '" href="' + esc(t.href) + '">' + inner + '</a>';
  }

  function arrowHTML(dir, label, path) {
    return '<button type="button" class="h9-arrow" data-dir="' + dir + '" ' +
           'aria-label="' + esc(label) + '">' +
           '<svg width="18" height="18" viewBox="0 0 18 18" fill="none" ' +
           'stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
           'stroke-linejoin="round" aria-hidden="true">' +
           '<path d="' + path + '"/></svg></button>';
  }

  function markup() {
    var art = cfg("heroIllustrationUrl") || assetUrl("filtrous-hero-illustration.png");

    var intro =
      '<div class="h9-intro">' +
        '<h1 class="h9-hd" id="h9-hd">' + esc(HEADLINE.lead) +
          '<span class="h9-hd-em">' + esc(HEADLINE.em) + '</span></h1>' +
        '<p class="h9-sub">' + esc(SUBHEAD) + '</p>' +
        (art
          ? '<img class="h9-art" src="' + esc(art) + '" alt="" width="723" ' +
            'height="418" decoding="async" fetchpriority="high" ' +
            'onerror="this.remove()" />'
          : "") +
        '<a class="h9-nudilink" href="' + esc(GUIDE_HREF) + '">' +
          'browse the nudibranches <span aria-hidden="true">&#8594;</span></a>' +
      '</div>';

    var rail =
      '<div class="h9-rail-region">' +
        '<div class="h9-rail-head">' +
          '<h2 class="h9-rail-h" id="h9-rail-h">Featured</h2>' +
          '<div class="h9-rail-nav">' +
            arrowHTML(-1, "Scroll featured offers left",  "M11.5 4L6.5 9l5 5") +
            arrowHTML(1,  "Scroll featured offers right", "M6.5 4l5 5-5 5") +
          '</div>' +
        '</div>' +
        '<ul class="h9-rail" role="list" aria-labelledby="h9-rail-h">' +
          TILES.map(function (t) {
            return '<li class="h9-cell">' + tileHTML(t) + "</li>";
          }).join("") +
        '</ul>' +
      '</div>';

    return intro + rail;
  }

  /* F7: the document's only h1 was a visually-hidden SEO string.
     It is still announced to screen readers, so the largest text
     on screen carried no heading semantics while a title-tag
     string did. The visible headline above is now the h1; this
     converts the old one to a paragraph, carrying its inline
     visually-hidden style across so it stays hidden and stays in
     the markup for crawlers. Delete this function and its call
     to restore the previous behaviour. */
  function demoteLegacyH1() {
    var olds = document.querySelectorAll("h1");
    for (var i = 0; i < olds.length; i++) {
      var h = olds[i];
      if (h.classList.contains("h9-hd")) continue;
      if (h.closest && h.closest(".fv3-hero9")) continue;
      var p = document.createElement("p");
      p.className = "fv3-seo-title";
      var st = h.getAttribute("style");
      if (st) p.setAttribute("style", st);
      p.textContent = h.textContent;
      h.parentNode.replaceChild(p, h);
    }
  }

  function wireRail(hero) {
    var rail = hero.querySelector(".h9-rail");
    var nav  = hero.querySelector(".h9-rail-nav");
    if (!rail || !nav || rail.dataset.wired === "1") return;
    rail.dataset.wired = "1";

    var buttons = nav.querySelectorAll(".h9-arrow");

    function step() {
      var cell = rail.querySelector(".h9-cell");
      if (!cell) return rail.clientWidth * 0.9;
      var gap = parseFloat(getComputedStyle(rail).columnGap || "16") || 16;
      return cell.getBoundingClientRect().width + gap;
    }

    function sync() {
      var max = rail.scrollWidth - rail.clientWidth - 1;
      /* Nothing to scroll: hide the controls rather than leave
         two permanently dead buttons in the tab order. */
      nav.hidden = max <= 0;
      for (var i = 0; i < buttons.length; i++) {
        var dir = Number(buttons[i].getAttribute("data-dir"));
        buttons[i].disabled = dir < 0 ? rail.scrollLeft <= 0
                                      : rail.scrollLeft >= max;
      }
    }

    for (var i = 0; i < buttons.length; i++) {
      buttons[i].addEventListener("click", function () {
        var dir = Number(this.getAttribute("data-dir"));
        rail.scrollBy({ left: dir * step(), behavior: "auto" });
      });
    }

    rail.addEventListener("scroll", function () {
      if (rail._raf) return;
      rail._raf = requestAnimationFrame(function () {
        rail._raf = null;
        sync();
      });
    });

    if (window.ResizeObserver) new ResizeObserver(sync).observe(rail);
    window.addEventListener("resize", sync);
    sync();
  }

  function placeHero() {
    var intro = document.querySelector(".fv3-home-intro");
    if (!intro) return;

    var hero = document.querySelector(".fv3-hero9");
    if (!hero) {
      hero = document.createElement("section");
      hero.className = "fv3-hero9";
      hero.setAttribute("aria-labelledby", "h9-hd");
      hero.innerHTML = markup();
      intro.parentNode.insertBefore(hero, intro);
      demoteLegacyH1();
    }
    wireRail(hero);
  }

  function boot() {
    placeHero();
    var mount = document.getElementById("fv3-app") || document.body;
    new MutationObserver(placeHero).observe(mount, { childList: true, subtree: true });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();

/* ------------------------------------------------------------
   Unchanged from the previous build: "Call for Quote" handling
   for OSK part numbers in search-result tables.
   ------------------------------------------------------------ */
(function(){"use strict";var QUOTE_HTML='<span class="fv3-quote" style="color:var(--navy,#082F4B);font-weight:400">Call for Quote</span>';function isOsk(sku){var s=String(sku||"").toUpperCase();return s.indexOf("OSK")>=0||/^(EXK|MDA|MDB|MNA|MNB|MXA)-/.test(s)}function fixTable(tbl){var tbody=tbl.tBodies&&tbl.tBodies[0];if(tbody){for(var rows=[],i=0;i<tbody.rows.length;i++){var tr=tbody.rows[i];tr.className&&tr.className.indexOf("fv3-group")!==-1||rows.push(tr)}if(rows.length){for(var flags=[],allOsk=!0,r=0;r<rows.length;r++){var partCell=rows[r].querySelector(".fv3-part"),osk=partCell?isOsk(partCell.textContent):!1;flags.push(osk),osk||(allOsk=!1)}if(allOsk&&tbl.tHead&&tbl.tHead.rows[0]&&!tbl.hasAttribute("data-oskq-head")){var headRow=tbl.tHead.rows[0],lastTh=headRow.cells[headRow.cells.length-1];lastTh&&/add/i.test(lastTh.textContent)&&lastTh.parentNode.removeChild(lastTh),tbl.setAttribute("data-oskq-head","1")}for(var j=0;j<rows.length;j++){var row=rows[j];if(row.getAttribute("data-oskq")!=="1"){if(!flags[j]){row.setAttribute("data-oskq","1");continue}var price=row.querySelector(".fv3-price");price&&(price.innerHTML=QUOTE_HTML);var last=row.cells[row.cells.length-1];allOsk?last&&last.parentNode.removeChild(last):last&&(last.innerHTML=""),row.setAttribute("data-oskq","1")}}}}}function pass(){for(var boxes=document.querySelectorAll("#fv3-app [data-hfsearch]"),i=0;i<boxes.length;i++){var tbl=boxes[i].querySelector("table.fv3-table");tbl&&fixTable(tbl)}}function boot(){pass();var mount=document.getElementById("fv3-app")||document.body;new MutationObserver(pass).observe(mount,{childList:!0,subtree:!0})}document.readyState==="loading"?document.addEventListener("DOMContentLoaded",boot):boot()})();
